'use client';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BadgeInfo,
  Building,
  Calendar,
  CheckCircle,
  Clock,
  DollarSign,
  ExternalLink,
  FileCheck,
  Globe,
  Heart,
  Home,
  Info,
  Landmark,
  Leaf,
  Lightbulb,
  Map,
  MapPin,
  Mountain,
  Navigation,
  PackageCheck,
  Palmtree,
  Plane,
  ShieldCheck,
  Utensils,
} from 'lucide-react';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';

import Container from '@/components/container';
import { cx } from '@/utils';

type TravelDetailsProps = {
  travelDetails: {
    destination?: string;
    destinationType?:
      | 'city'
      | 'country'
      | 'region'
      | 'landmark'
      | 'nationalPark'
      | 'beach'
      | 'mountain'
      | 'island'
      | 'other';
    tripType?: Array<string>;
    bestTimeToVisit?: Array<string>;
    budgetRange?: 'budget' | 'midRange' | 'luxury';
    duration?: string;
    mainActivities?: Array<string>;
    coordinates?: {
      latitude?: number;
      longitude?: number;
    };
    itinerary?: Array<{
      day?: string;
      activities?: Array<string>;
      accommodation?: string;
      _key: string;
    }>;
    accommodationSuggestions?: Array<{
      name?: string;
      type?: string;
      priceRange?: string;
      link?: string;
      notes?: string;
      _key: string;
    }>;
    transportationTips?: Array<string>;
    localCuisine?: Array<string>;
    packingEssentials?: Array<string>;
    travelTips?: Array<string>;
    sustainabilityNotes?: string;
    visaRequirements?: string;
    safetyInfo?: string;
    relatedDestinations?: Array<string>;
  };
};

// Budget icon display with animation
const BudgetIndicator = ({ budgetRange }) => {
  let dollarCount = 1;
  if (budgetRange === 'midRange') dollarCount = 2;
  if (budgetRange === 'luxury') dollarCount = 3;

  return (
    <div className="flex">
      {[1, 2, 3].map(val => (
        <motion.div
          key={val}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{
            opacity: val <= dollarCount ? 1 : 0.3,
            scale: val <= dollarCount ? 1 : 0.8,
            y: val <= dollarCount ? [0, -2, 0] : 0,
          }}
          transition={{
            duration: 0.4,
            delay: val * 0.1,
            y: {
              repeat: val <= dollarCount ? Infinity : 0,
              repeatType: 'reverse',
              duration: 1,
              repeatDelay: 0.5,
            },
          }}
        >
          <DollarSign
            className={`h-4 w-4 ${val <= dollarCount ? 'text-white' : 'text-white/30'}`}
          />
        </motion.div>
      ))}
    </div>
  );
};

// Destination type icon
const getDestinationTypeIcon = destinationType => {
  switch (destinationType) {
    case 'city':
      return <Building className="mr-2 h-5 w-5" />;
    case 'country':
      return <Map className="mr-2 h-5 w-5" />;
    case 'region':
      return <Globe className="mr-2 h-5 w-5" />;
    case 'landmark':
      return <Landmark className="mr-2 h-5 w-5" />;
    case 'nationalPark':
      return <Leaf className="mr-2 h-5 w-5" />;
    case 'beach':
      return <Palmtree className="mr-2 h-5 w-5" />;
    case 'mountain':
      return <Mountain className="mr-2 h-5 w-5" />;
    case 'island':
      return <Palmtree className="mr-2 h-5 w-5" />;
    default:
      return <Plane className="mr-2 h-5 w-5" />;
  }
};

// Enhanced Map Card Component
const LocationMapCard = ({ coordinates }) => {
  const { latitude, longitude } = coordinates;
  const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <div className="mt-12" ref={ref}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
        className="mx-auto max-w-xl overflow-hidden rounded-xl border border-teal-100 bg-white shadow-lg dark:border-teal-900/30 dark:bg-gray-800"
      >
        <div className="border-b border-teal-100 bg-gradient-to-r from-teal-50 to-blue-50 p-4 dark:border-teal-900/30 dark:from-teal-900/20 dark:to-blue-900/20">
          <h3 className="flex items-center justify-center text-xl font-semibold text-gray-800 dark:text-white">
            <MapPin className="mr-2 h-5 w-5 text-teal-600 dark:text-teal-400" />
            Exact Location
          </h3>
        </div>

        <div className="p-6">
          <div className="mb-6 h-40 w-full rounded-lg bg-teal-50 dark:bg-teal-900/20">
            <div className="relative h-full w-full overflow-hidden rounded-lg border border-teal-100 dark:border-teal-900/30">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-24 w-24 animate-pulse rounded-full bg-teal-500/10"></div>
                <div className="absolute h-16 w-16 animate-pulse rounded-full bg-teal-500/20"></div>
                <div className="absolute h-8 w-8 animate-pulse rounded-full bg-teal-500/30"></div>
                <MapPin className="absolute h-6 w-6 text-teal-600 dark:text-teal-400" />
              </div>
              <div className="bg-grid-pattern absolute inset-0 opacity-5"></div>
            </div>
          </div>

          <div className="mb-6 space-y-3">
            <div className="flex items-center justify-between rounded-md bg-teal-50 p-3 dark:bg-teal-900/20">
              <div className="flex items-center">
                <Navigation className="mr-2 h-4 w-4 text-teal-600 dark:text-teal-400" />
                <span className="font-medium text-gray-700 dark:text-gray-300">Latitude</span>
              </div>
              <span className="rounded-md bg-white px-3 py-1 font-mono text-sm text-teal-800 shadow-sm dark:bg-gray-700 dark:text-teal-200">
                {latitude}
              </span>
            </div>

            <div className="flex items-center justify-between rounded-md bg-teal-50 p-3 dark:bg-teal-900/20">
              <div className="flex items-center">
                <Navigation className="mr-2 h-4 w-4 rotate-90 transform text-teal-600 dark:text-teal-400" />
                <span className="font-medium text-gray-700 dark:text-gray-300">Longitude</span>
              </div>
              <span className="rounded-md bg-white px-3 py-1 font-mono text-sm text-teal-800 shadow-sm dark:bg-gray-700 dark:text-teal-200">
                {longitude}
              </span>
            </div>
          </div>

          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-teal-500 to-blue-500 px-4 py-3 font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg"
          >
            <ExternalLink className="mr-2 h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
            <span className="transition-transform duration-300 group-hover:translate-x-1">
              View on Google Maps
            </span>
          </a>
        </div>
      </motion.div>
    </div>
  );
};

// Main TravelDetails component
const TravelDetails = ({ travelDetails }: TravelDetailsProps) => {
  const [headerRef, headerInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [overviewRef, overviewInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [itineraryRef, itineraryInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  // For expandable sections
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  if (!travelDetails) return null;

  return (
    <div className="pt-6">
      <Container>
        {/* Enhanced Header Section */}
        <div className="mb-10 text-center" ref={headerRef}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            {travelDetails.destination && (
              <div className="mb-6">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="mx-auto mb-4 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-teal-100 to-blue-100 p-3 dark:from-teal-900/40 dark:to-blue-900/40"
                >
                  <div className="rounded-full bg-gradient-to-r from-teal-500 to-blue-500 p-3 text-white">
                    {getDestinationTypeIcon(travelDetails.destinationType)}
                  </div>
                </motion.div>

                <h2 className="relative font-serif text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
                  <span className="relative z-10">
                    {travelDetails.destination}
                    <span className="absolute -bottom-1 left-0 h-3 w-full bg-teal-400/20 dark:bg-teal-700/30"></span>
                  </span>{' '}
                  <span className="bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent dark:from-teal-400 dark:to-blue-400">
                    Travel Guide
                  </span>
                </h2>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-center gap-4">
              {travelDetails.duration && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="flex items-center rounded-full bg-gradient-to-r from-teal-50 to-teal-100 px-5 py-2.5 text-sm font-medium shadow-sm dark:from-teal-900/30 dark:to-teal-800/20"
                >
                  <Clock className="mr-2 inline-block h-4 w-4 text-teal-600 dark:text-teal-400" />
                  <span className="text-teal-800 dark:text-teal-200">
                    <span className="font-semibold">Ideal Duration:</span> {travelDetails.duration}
                  </span>
                </motion.div>
              )}

              {travelDetails.budgetRange && (
                <motion.div
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  className="flex items-center rounded-full bg-gradient-to-r from-blue-50 to-blue-100 px-5 py-2.5 text-sm font-medium shadow-sm dark:from-blue-900/30 dark:to-blue-800/20"
                >
                  <span className="mr-2 text-blue-800 dark:text-blue-200">
                    <span className="font-semibold">
                      {travelDetails.budgetRange === 'budget'
                        ? 'Budget-Friendly'
                        : travelDetails.budgetRange === 'midRange'
                          ? 'Mid-Range'
                          : 'Luxury'}
                    </span>
                  </span>
                  <BudgetIndicator budgetRange={travelDetails.budgetRange} />
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Overview Cards Section - Enhanced with animations */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3" ref={overviewRef}>
          {/* Trip Overview Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={overviewInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4 }}
            className="group overflow-hidden rounded-xl border border-teal-100 bg-white shadow-md transition-all duration-300 hover:shadow-lg dark:border-teal-900/30 dark:bg-gray-800"
          >
            <div className="border-b border-teal-100 bg-gradient-to-r from-teal-50 to-blue-50 px-4 py-4 dark:border-teal-900/30 dark:from-teal-900/20 dark:to-blue-900/20">
              <h3 className="flex items-center font-medium text-gray-800 dark:text-white">
                <Globe className="mr-2 h-5 w-5 text-teal-600 dark:text-teal-400" />
                Trip Overview
              </h3>
            </div>
            <div className="p-5">
              <ul className="space-y-4">
                {travelDetails.tripType && travelDetails.tripType.length > 0 && (
                  <li className="flex items-start">
                    <BadgeInfo className="mt-0.5 mr-3 h-4 w-4 flex-shrink-0 text-teal-600 dark:text-teal-400" />
                    <div>
                      <span className="block font-medium text-gray-700 dark:text-gray-300">
                        Type:
                      </span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {travelDetails.tripType.map((type, index) => (
                          <span
                            key={index}
                            className="inline-flex rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-medium text-teal-800 dark:bg-teal-900/40 dark:text-teal-200"
                          >
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                  </li>
                )}

                {travelDetails.bestTimeToVisit && travelDetails.bestTimeToVisit.length > 0 && (
                  <li className="flex items-start">
                    <Clock className="mt-0.5 mr-3 h-4 w-4 flex-shrink-0 text-teal-600 dark:text-teal-400" />
                    <div>
                      <span className="block font-medium text-gray-700 dark:text-gray-300">
                        Best Time to Visit:
                      </span>
                      <p className="mt-1 text-gray-600 dark:text-gray-400">
                        {travelDetails.bestTimeToVisit.join(', ')}
                      </p>
                    </div>
                  </li>
                )}

                {travelDetails.mainActivities && travelDetails.mainActivities.length > 0 && (
                  <li className="flex items-start">
                    <Lightbulb className="mt-0.5 mr-3 h-4 w-4 flex-shrink-0 text-teal-600 dark:text-teal-400" />
                    <div>
                      <span className="block font-medium text-gray-700 dark:text-gray-300">
                        Main Activities:
                      </span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {travelDetails.mainActivities.map((activity, index) => (
                          <span
                            key={index}
                            className="inline-flex rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
                          >
                            {activity}
                          </span>
                        ))}
                      </div>
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </motion.div>

          {/* Practical Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={overviewInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="group overflow-hidden rounded-xl border border-teal-100 bg-white shadow-md transition-all duration-300 hover:shadow-lg dark:border-teal-900/30 dark:bg-gray-800"
          >
            <div className="border-b border-teal-100 bg-gradient-to-r from-teal-50 to-blue-50 px-4 py-4 dark:border-teal-900/30 dark:from-teal-900/20 dark:to-blue-900/20">
              <h3 className="flex items-center font-medium text-gray-800 dark:text-white">
                <Info className="mr-2 h-5 w-5 text-teal-600 dark:text-teal-400" />
                Practical Information
              </h3>
            </div>
            <div className="p-5">
              <ul className="space-y-4">
                {travelDetails.visaRequirements && (
                  <li className="flex items-start">
                    <FileCheck className="mt-0.5 mr-3 h-4 w-4 flex-shrink-0 text-teal-600 dark:text-teal-400" />
                    <div>
                      <span className="block font-medium text-gray-700 dark:text-gray-300">
                        Visa:
                      </span>
                      <p className="mt-1 text-gray-600 dark:text-gray-400">
                        {travelDetails.visaRequirements}
                      </p>
                    </div>
                  </li>
                )}

                {travelDetails.safetyInfo && (
                  <li className="flex items-start">
                    <ShieldCheck className="mt-0.5 mr-3 h-4 w-4 flex-shrink-0 text-teal-600 dark:text-teal-400" />
                    <div>
                      <span className="block font-medium text-gray-700 dark:text-gray-300">
                        Safety:
                      </span>
                      <p className="mt-1 text-gray-600 dark:text-gray-400">
                        {travelDetails.safetyInfo}
                      </p>
                    </div>
                  </li>
                )}

                {travelDetails.transportationTips &&
                  travelDetails.transportationTips.length > 0 && (
                    <li className="flex items-start">
                      <Plane className="mt-0.5 mr-3 h-4 w-4 flex-shrink-0 text-teal-600 dark:text-teal-400" />
                      <div>
                        <span className="block font-medium text-gray-700 dark:text-gray-300">
                          Transportation:
                        </span>
                        <p className="mt-1 text-gray-600 dark:text-gray-400">
                          {travelDetails.transportationTips[0]}
                          {travelDetails.transportationTips.length > 1 && (
                            <button
                              onClick={() => toggleSection('transportation')}
                              className="ml-1 text-teal-600 hover:underline dark:text-teal-400"
                            >
                              {expandedSection === 'transportation' ? 'Show less' : 'Show more...'}
                            </button>
                          )}
                        </p>
                        <AnimatePresence>
                          {expandedSection === 'transportation' &&
                            travelDetails.transportationTips.length > 1 && (
                              <motion.ul
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                className="mt-2 space-y-2 overflow-hidden"
                              >
                                {travelDetails.transportationTips.slice(1).map((tip, index) => (
                                  <motion.li
                                    key={index}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="flex text-gray-600 dark:text-gray-400"
                                  >
                                    <span className="mr-2">•</span>
                                    {tip}
                                  </motion.li>
                                ))}
                              </motion.ul>
                            )}
                        </AnimatePresence>
                      </div>
                    </li>
                  )}

                {travelDetails.sustainabilityNotes && (
                  <li className="flex items-start">
                    <Leaf className="mt-0.5 mr-3 h-4 w-4 flex-shrink-0 text-teal-600 dark:text-teal-400" />
                    <div>
                      <span className="block font-medium text-gray-700 dark:text-gray-300">
                        Sustainability:
                      </span>
                      <p className="mt-1 text-gray-600 dark:text-gray-400">
                        {travelDetails.sustainabilityNotes}
                      </p>
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </motion.div>

          {/* What to Pack Card */}
          {travelDetails.packingEssentials && travelDetails.packingEssentials.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={overviewInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="group overflow-hidden rounded-xl border border-teal-100 bg-white shadow-md transition-all duration-300 hover:shadow-lg dark:border-teal-900/30 dark:bg-gray-800"
            >
              <div className="border-b border-teal-100 bg-gradient-to-r from-teal-50 to-blue-50 px-4 py-4 dark:border-teal-900/30 dark:from-teal-900/20 dark:to-blue-900/20">
                <h3 className="flex items-center font-medium text-gray-800 dark:text-white">
                  <PackageCheck className="mr-2 h-5 w-5 text-teal-600 dark:text-teal-400" />
                  What to Pack
                </h3>
              </div>
              <div className="p-5">
                <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {travelDetails.packingEssentials.slice(0, 6).map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={overviewInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.3, delay: 0.2 + index * 0.05 }}
                      className="flex items-start"
                    >
                      <CheckCircle className="mt-0.5 mr-2 h-4 w-4 flex-shrink-0 text-teal-600 dark:text-teal-400" />
                      <span className="text-gray-700 dark:text-gray-300">{item}</span>
                    </motion.li>
                  ))}
                </ul>

                {travelDetails.packingEssentials.length > 6 && (
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    onClick={() => toggleSection('packing')}
                    className="mt-3 w-full rounded-md bg-teal-50 px-4 py-2 text-sm font-medium text-teal-700 transition-colors hover:bg-teal-100 dark:bg-teal-900/20 dark:text-teal-300 dark:hover:bg-teal-900/30"
                  >
                    {expandedSection === 'packing'
                      ? 'Show less'
                      : `Show ${travelDetails.packingEssentials.length - 6} more items`}
                  </motion.button>
                )}

                <AnimatePresence>
                  {expandedSection === 'packing' && (
                    <motion.ul
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-3 grid grid-cols-1 gap-2 overflow-hidden sm:grid-cols-2"
                    >
                      {travelDetails.packingEssentials.slice(6).map((item, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="flex items-start"
                        >
                          <CheckCircle className="mt-0.5 mr-2 h-4 w-4 flex-shrink-0 text-teal-600 dark:text-teal-400" />
                          <span className="text-gray-700 dark:text-gray-300">{item}</span>
                        </motion.li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </div>

        {/* Itinerary Section */}
        {travelDetails.itinerary && travelDetails.itinerary.length > 0 && (
          <div className="mt-16" ref={itineraryRef}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={itineraryInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
            >
              <div className="mb-10 text-center">
                <span className="mb-3 inline-flex rounded-full bg-teal-100 px-3 py-1 text-xs font-medium text-teal-800 dark:bg-teal-900/40 dark:text-teal-200">
                  TRIP PLANNER
                </span>
                <h3 className="flex items-center justify-center text-2xl font-bold text-gray-900 md:text-3xl dark:text-white">
                  <Calendar className="mr-3 h-6 w-6 text-teal-600 dark:text-teal-400" />
                  <span>Day-by-Day Itinerary</span>
                </h3>
                <p className="mx-auto mt-3 max-w-2xl text-sm text-gray-600 md:text-base dark:text-gray-400">
                  Follow this carefully curated itinerary to make the most of your visit to{' '}
                  {travelDetails.destination}.
                </p>
              </div>
            </motion.div>

            <div className="relative mt-12">
              {/* Animated Timeline line */}
              <div className="absolute top-0 left-4 h-full w-0.5 bg-gradient-to-b from-teal-500 to-blue-500 md:left-1/2 md:-translate-x-1/2 md:transform">
                <div className="animate-travelPulse absolute top-0 h-24 w-full bg-gradient-to-b from-teal-300/0 via-teal-300/70 to-teal-300/0"></div>
              </div>

              {travelDetails.itinerary.map((day, index) => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const [dayRef, dayInView] = useInView({ triggerOnce: true, threshold: 0.1 });

                return (
                  <div
                    key={day._key}
                    ref={dayRef}
                    className={cx(
                      'relative mb-16 flex flex-col md:flex-row',
                      index % 2 === 0 ? 'md:flex-row-reverse' : '',
                    )}
                  >
                    <motion.div
                      initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                      animate={dayInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                      className="flex items-start justify-center md:w-1/2"
                    >
                      <div
                        className={`w-full max-w-md rounded-xl border border-teal-100 bg-white p-6 shadow-lg transition-all duration-300 hover:shadow-xl dark:border-teal-900/30 dark:bg-gray-800 ${
                          index % 2 === 0 ? 'md:mr-12' : 'md:ml-12'
                        }`}
                      >
                        <div className="mb-4 inline-flex rounded-full bg-gradient-to-r from-teal-500 to-blue-500 px-3 py-1 text-sm font-semibold text-white">
                          {day.day}
                        </div>

                        {day.activities && day.activities.length > 0 && (
                          <div className="mb-6">
                            <h4 className="mb-3 font-medium text-gray-900 dark:text-white">
                              <Calendar className="mr-2 inline h-4 w-4 text-teal-600 dark:text-teal-400" />
                              Today&apos;s Activities
                            </h4>
                            <ul className="space-y-3">
                              {day.activities.map((activity, actIndex) => (
                                <motion.li
                                  key={actIndex}
                                  initial={{ opacity: 0, x: -5 }}
                                  animate={dayInView ? { opacity: 1, x: 0 } : {}}
                                  transition={{ duration: 0.3, delay: 0.2 + actIndex * 0.1 }}
                                  className="flex items-start rounded-md bg-teal-50/50 p-2 transition-colors hover:bg-teal-50 dark:bg-teal-900/10 dark:hover:bg-teal-900/20"
                                >
                                  <div className="mr-3 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-teal-500 to-blue-500 text-white">
                                    <span className="text-xs font-bold">{actIndex + 1}</span>
                                  </div>
                                  <span className="text-gray-800 dark:text-gray-200">
                                    {activity}
                                  </span>
                                </motion.li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {day.accommodation && (
                          <div className="rounded-lg border border-teal-100 bg-teal-50/50 p-3 dark:border-teal-900/30 dark:bg-teal-900/10">
                            <h5 className="mb-1 font-medium text-gray-900 dark:text-white">
                              <Home className="mr-2 inline h-4 w-4 text-teal-600 dark:text-teal-400" />
                              Where to Stay
                            </h5>
                            <p className="text-gray-700 dark:text-gray-300">{day.accommodation}</p>
                          </div>
                        )}
                      </div>
                    </motion.div>

                    {/* Timeline dots with enhanced styling */}
                    <div className="absolute top-0 left-4 z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-md md:left-1/2 md:-translate-x-1/2 md:transform">
                      <span className="text-xs font-bold">{index + 1}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Accommodation Suggestions */}
        {travelDetails.accommodationSuggestions &&
          travelDetails.accommodationSuggestions.length > 0 && (
            <div className="mt-20">
              <div className="mb-10 text-center">
                <span className="mb-3 inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                  ACCOMMODATIONS
                </span>
                <h3 className="flex items-center justify-center text-2xl font-bold text-gray-900 md:text-3xl dark:text-white">
                  <Home className="mr-3 h-6 w-6 text-blue-600 dark:text-blue-400" />
                  <span>Where to Stay</span>
                </h3>
                <p className="mx-auto mt-3 max-w-2xl text-sm text-gray-600 md:text-base dark:text-gray-400">
                  Recommended accommodations for different budgets and preferences in{' '}
                  {travelDetails.destination}.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {travelDetails.accommodationSuggestions.map((place, index) => (
                  <motion.div
                    key={place._key}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="group overflow-hidden rounded-xl border border-blue-100 bg-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-blue-900/30 dark:bg-gray-800"
                  >
                    <div className="border-b border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 dark:border-blue-900/30 dark:from-blue-900/20 dark:to-indigo-900/20">
                      <h4 className="font-serif text-lg font-semibold text-gray-900 dark:text-white">
                        {place.name}
                      </h4>
                    </div>

                    <div className="p-5">
                      <div className="mb-4 flex flex-wrap gap-2">
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                          {place.type}
                        </span>
                        <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200">
                          {place.priceRange}
                        </span>
                      </div>

                      {place.notes && (
                        <p className="mb-5 text-sm text-gray-600 dark:text-gray-300">
                          {place.notes}
                        </p>
                      )}

                      {place.link && (
                        <a
                          href={place.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-2 font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg"
                        >
                          <span className="transition-transform duration-300 group-hover:translate-x-1">
                            Check Availability
                          </span>
                          <ExternalLink className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </a>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

        {/* Local Cuisine */}
        {travelDetails.localCuisine && travelDetails.localCuisine.length > 0 && (
          <div className="mt-20">
            <div className="mb-10 text-center">
              <span className="mb-3 inline-flex rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-800 dark:bg-orange-900/40 dark:text-orange-200">
                LOCAL CUISINE
              </span>
              <h3 className="flex items-center justify-center text-2xl font-bold text-gray-900 md:text-3xl dark:text-white">
                <Utensils className="mr-3 h-6 w-6 text-orange-600 dark:text-orange-400" />
                <span>What to Eat</span>
              </h3>
              <p className="mx-auto mt-3 max-w-2xl text-sm text-gray-600 md:text-base dark:text-gray-400">
                Don&apos;t miss these local culinary delights during your visit to{' '}
                {travelDetails.destination}.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {travelDetails.localCuisine.map((dish, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="group flex items-center overflow-hidden rounded-xl border border-orange-100 bg-white p-4 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:border-orange-900/30 dark:bg-gray-800"
                >
                  <div className="mr-4 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md transition-all duration-300 group-hover:scale-110">
                    <Utensils className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="block font-medium text-gray-900 dark:text-white">{dish}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Local specialty
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Travel Tips */}
        {travelDetails.travelTips && travelDetails.travelTips.length > 0 && (
          <div className="mt-20">
            <div className="mb-10 text-center">
              <span className="mb-3 inline-flex rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900/40 dark:text-purple-200">
                INSIDER TIPS
              </span>
              <h3 className="flex items-center justify-center text-2xl font-bold text-gray-900 md:text-3xl dark:text-white">
                <Lightbulb className="mr-3 h-6 w-6 text-purple-600 dark:text-purple-400" />
                <span>Pro Travel Tips</span>
              </h3>
              <p className="mx-auto mt-3 max-w-2xl text-sm text-gray-600 md:text-base dark:text-gray-400">
                Valuable advice to enhance your experience in {travelDetails.destination}.
              </p>
            </div>

            <div className="overflow-hidden rounded-xl border border-purple-100 bg-gradient-to-br from-purple-50 to-indigo-50 p-8 shadow-lg dark:border-purple-900/30 dark:bg-gradient-to-br dark:from-purple-900/20 dark:to-indigo-900/10">
              <ul className="grid grid-cols-1 gap-5 md:grid-cols-2">
                {travelDetails.travelTips.map((tip, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="flex items-start"
                  >
                    <div className="mr-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md">
                      <span className="text-xs font-bold">{index + 1}</span>
                    </div>
                    <div className="rounded-lg bg-white/70 p-3 shadow-sm dark:bg-white/5">
                      <p className="text-gray-800 dark:text-gray-200">{tip}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Related Destinations */}
        {travelDetails.relatedDestinations && travelDetails.relatedDestinations.length > 0 && (
          <div className="mt-20">
            <div className="mb-10 text-center">
              <span className="mb-3 inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-200">
                EXPLORE MORE
              </span>
              <h3 className="flex items-center justify-center text-2xl font-bold text-gray-900 md:text-3xl dark:text-white">
                <Navigation className="mr-3 h-6 w-6 text-green-600 dark:text-green-400" />
                <span>Related Destinations</span>
              </h3>
              <p className="mx-auto mt-3 max-w-2xl text-sm text-gray-600 md:text-base dark:text-gray-400">
                Consider adding these nearby destinations to your travel itinerary.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              {travelDetails.relatedDestinations.map((destination, index) => (
                <motion.button
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center rounded-full bg-gradient-to-r from-green-500 to-emerald-600 px-5 py-2.5 text-white shadow-md transition-colors"
                >
                  <Globe className="mr-2 h-4 w-4" />
                  {destination}
                </motion.button>
              ))}
            </div>
          </div>
        )}

        {/* Map Card */}
        {travelDetails.coordinates && (
          <LocationMapCard
            coordinates={{
              latitude: travelDetails.coordinates.latitude,
              longitude: travelDetails.coordinates.longitude,
            }}
          />
        )}

        {/* Save Button */}
        <motion.div
          className="mt-16 flex justify-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <button className="group flex items-center rounded-full bg-gradient-to-r from-teal-500 to-blue-500 px-8 py-3 font-medium text-white shadow-lg transition-all duration-300 hover:shadow-xl">
            <Heart className="mr-2 h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
            <span className="transition-transform duration-300 group-hover:translate-x-1">
              Save This Guide
            </span>
          </button>
        </motion.div>
      </Container>
    </div>
  );
};

export default TravelDetails;
